import { useEffect, useMemo, useState } from 'react'
import {
  Accordion,
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core'
import {
  IconAlertTriangle,
  IconExternalLink,
  IconHandStop,
  IconLock,
  IconPlayerPlay,
  IconRefresh,
} from '@tabler/icons-react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { PageTitle, SectionTitle, HairlineDivider } from '@lighthouse/design'
import { useEtlJob, useEtlJobCatalog, useEtlJobTriggerRequests } from '../hooks/useEtlJobs'
import { useEtlHalts } from '../hooks/useEtlHalts'
import { isEntraConfigured } from '../auth'
import { RunJobModal } from '../components/jobs/RunJobModal'
import { HaltPanel } from '../components/jobs/HaltPanel'
import { runDeniedRemedy } from '../components/jobs/haltClearFormat'
import { RestrictedState } from '../components/access/RestrictedState'
import { groupJobs } from '../components/jobs/groupJobs'
import { OutcomeBadge } from '../components/execution-history/ExecutionList'
import {
  JOB_QUERY_KEY,
  catalogBehindHalts,
  catalogView,
  formatRequestParameters,
  linkProvenance,
  requestHistoryCaption,
  requestHistoryView,
  requestRunLink,
  requestRunOutcome,
  requestStatusDetailText,
  runDashCaption,
  runRefusal,
} from '../components/jobs/requestFormat'
import { initialParameterValues } from '../services/etlJobsApi'
import { hangfireDashboardUrl, hangfireJobUrl } from '../utils/hangfireUrl'
import { NOT_MEASURED, errorKind, errorSummary, whenLabel } from '../utils/format'
import type { EtlJob, EtlJobTriggerRequest } from '../types/etlJobs'

/**
 * Scheduler — what can be run, and running it.
 *
 * The job list is served by the API from a catalogue Automaton publishes on startup, so this page
 * enumerates nothing itself: a newly ported entity appears here without a change to this app. The
 * Hangfire dashboard stays linked for what it is genuinely good at — queue state, retries, failed-job
 * payloads — rather than being the place domain filters live.
 *
 * A deep link — `/jobs?job=<jobId>[&startDate=&endDate=&module=]` — opens that job's run form with the
 * link's values in the fields the job declares, each marked as coming from the link. It never submits.
 */
export function JobsPage() {
  const { data: catalog, isLoading, isError, error, refetch, isFetching } = useEtlJobCatalog()
  const requests = useEtlJobTriggerRequests()
  // The same query HaltPanel reads (one request): used here only to notice the catalogue falling behind.
  const { data: halts } = useEtlHalts(true)
  const [running, setRunning] = useState<EtlJob | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()

  // Permission is absent on an older API or a failed load. Treating that as "allowed" keeps the page
  // working against a server that predates this field: the trigger endpoint is still authorized, so the
  // worst case is a 403 on submit -- the same behaviour as before -- rather than a console locked out by
  // a missing field.
  const canRun = catalog?.permission ? catalog.permission.canTriggerJobs : true
  const denialReason = catalog?.permission?.deniedReason ?? undefined

  const dashboardUrl = hangfireDashboardUrl()

  const grouped = useMemo(() => groupJobs(catalog?.jobs ?? []), [catalog])

  // The catalogue is not polled (the rail shares it), but it is what says which categories a halt still
  // lets through. When the polled halt list disagrees with it about how many halts are blocking, re-read
  // it, so the Run buttons follow a halt landing or being cleared without a reload.
  useEffect(() => {
    if (catalogBehindHalts(halts, catalog)) void refetch()
  }, [halts, catalog, refetch])

  /** Why the server would refuse this job now: the caller's permission first, then an active halt. */
  const refusalFor = (job: EtlJob): string | null => runRefusal(job, canRun, denialReason, catalog?.activeHalt)

  const view = catalogView(catalog, isLoading, isError)

  // --- Deep link -------------------------------------------------------------------------------------
  const linkedJobId = searchParams.get(JOB_QUERY_KEY)
  const linkedFromCatalog =
    linkedJobId !== null && catalog ? (catalog.jobs.find((j) => j.jobId === linkedJobId) ?? null) : null
  // Asked for only when the catalogue cannot answer: it failed to load, or it does not list the id.
  const lookupNeeded = linkedJobId !== null && !linkedFromCatalog && (isError || !!catalog)
  const jobLookup = useEtlJob(linkedJobId, lookupNeeded)
  const linkedJob = linkedFromCatalog ?? jobLookup.data ?? null

  const link = useMemo(
    () =>
      linkedJob
        ? linkProvenance(linkedJob.parameters, initialParameterValues(linkedJob.parameters), searchParams)
        : null,
    [linkedJob, searchParams]
  )

  const modalJob = linkedJob ?? running

  const closeModal = () => {
    setRunning(null)
    // Closing forgets the link: the query string is what opened the form, so leaving it would reopen it.
    if ([...searchParams.keys()].length > 0) setSearchParams(new URLSearchParams(), { replace: true })
  }

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-start">
        <PageTitle subtitle="What is registered, and what you can run">Scheduler</PageTitle>
        <Group gap="xs">
          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconRefresh size={14} />}
            loading={isFetching}
            onClick={() => refetch()}
          >
            Refresh
          </Button>
          {dashboardUrl ? (
            <Anchor href={dashboardUrl} target="_blank" rel="noopener noreferrer" size="sm">
              Hangfire dashboard <IconExternalLink size={12} />
            </Anchor>
          ) : (
            <Text size="sm" c="dimmed">
              Hangfire dashboard (address not configured)
            </Text>
          )}
        </Group>
      </Group>

      <HaltPanel catalog={catalog} entraConfigured={isEntraConfigured()} />

      {catalog?.permission && !catalog.permission.canTriggerJobs && (
        // The server's answer, not a claim inspected here. Shown rather than silently hiding the
        // buttons: an operator who cannot run a job should be told why, and "the button vanished"
        // is indistinguishable from "the page is broken".
        <Alert icon={<IconLock size={16} />} color="yellow" title="You cannot run jobs from this session">
          <Text size="sm">
            {catalog.permission.deniedReason}
            {/* The server cannot know this build has no Entra sign-in, so its "Sign in" needs the local truth. */}
            {runDeniedRemedy(isEntraConfigured()) && <> {runDeniedRemedy(isEntraConfigured())}</>}
          </Text>
        </Alert>
      )}

      {linkedJobId !== null && !linkedJob && (
        <LinkedJobStatus
          jobId={linkedJobId}
          lookupEnabled={lookupNeeded}
          isLoading={jobLookup.isLoading}
          error={jobLookup.isError ? jobLookup.error : null}
          onDismiss={closeModal}
        />
      )}

      {isError && catalog && (
        <Alert color="yellow" variant="light" title="Could not refresh the job catalogue">
          <Text size="sm">
            {errorSummary(error) ?? 'The request failed.'} The jobs below are the catalogue as last loaded.
          </Text>
        </Alert>
      )}

      {view === 'failed' ? (
        errorKind(error) === 'forbidden' ? (
          <RestrictedState surface="The job catalogue" />
        ) : (
          <Alert color="red" title="Could not load the job catalogue">
            {errorSummary(error) ?? 'The request failed.'}
          </Alert>
        )
      ) : view === 'loading' ? (
        // "No payload yet" is loading too: between retries the flags read "not loading, not errored"
        // with no data, and rendering then would show an empty catalogue that is not one.
        <Loader />
      ) : view === 'empty' ? (
        <Alert color="yellow" title="No jobs are published">
          Automaton writes the catalogue when it starts. An empty list usually means the worker has not
          started against this database yet.
        </Alert>
      ) : (
        grouped.map(({ key, heading, jobs, modules, showsCategory }) => (
          <Stack key={key} gap="sm">
            <SectionTitle>{heading}</SectionTitle>
            {modules ? (
              <Accordion variant="separated" multiple>
                {modules.map(({ module, jobs: moduleJobs }) => (
                  <Accordion.Item key={module} value={module}>
                    <Accordion.Control>
                      <Group gap="xs">
                        <Text fw={500}>{module}</Text>
                        <Badge size="sm" variant="light">
                          {moduleJobs.length}
                        </Badge>
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <JobTable
                        jobs={moduleJobs}
                        onRun={setRunning}
                        showLevel
                        canRun={canRun}
                        refusalFor={refusalFor}
                      />
                    </Accordion.Panel>
                  </Accordion.Item>
                ))}
              </Accordion>
            ) : (
              <Card withBorder padding="sm">
                <JobTable
                  jobs={jobs}
                  onRun={setRunning}
                  showCategory={showsCategory}
                  canRun={canRun}
                  refusalFor={refusalFor}
                />
              </Card>
            )}
          </Stack>
        ))
      )}

      <Stack gap="sm">
        <HairlineDivider />
        <SectionTitle>Recent run requests</SectionTitle>
        <Text size="xs" c="dimmed">
          {requestHistoryCaption(requests.data ? requests.data.length : null)}
        </Text>
        <RequestHistory
          requests={requests.data}
          isLoading={requests.isLoading}
          error={requests.isError ? requests.error : null}
          jobNames={new Map((catalog?.jobs ?? []).map((j) => [j.jobId, j.displayName]))}
        />
      </Stack>

      {catalog?.publishedAt && (
        <Text size="xs" c="dimmed">
          Catalogue published {new Date(catalog.publishedAt).toLocaleString()} — it is rewritten each
          time the worker starts.
        </Text>
      )}

      <RunJobModal
        job={modalJob}
        hangfireDashboardUrl={dashboardUrl}
        onClose={closeModal}
        initialValues={linkedJob ? link?.values : null}
        linkedNames={linkedJob ? link?.linkedNames : undefined}
        openedFromLink={linkedJob !== null}
        ignoredLinkValues={linkedJob ? link?.ignored : undefined}
        resume={catalog?.resume ?? null}
        entraConfigured={isEntraConfigured()}
        blockedReason={modalJob ? refusalFor(modalJob) : null}
      />
    </Stack>
  )
}

/** What became of a deep link whose job is not (yet) known. */
function LinkedJobStatus({
  jobId,
  lookupEnabled,
  isLoading,
  error,
  onDismiss,
}: {
  jobId: string
  lookupEnabled: boolean
  isLoading: boolean
  error: unknown
  onDismiss: () => void
}) {
  // Still waiting on the catalogue: the page's own loader says so.
  if (!lookupEnabled) return null

  if (error) {
    const missing = errorKind(error) === 'not-found'
    return (
      <Alert
        color={missing ? 'yellow' : 'red'}
        title={missing ? `No job ${jobId} is registered` : `Could not open job ${jobId}`}
        withCloseButton
        onClose={onDismiss}
      >
        <Text size="sm">
          {missing
            ? 'The link names a job this deployment does not have. Nothing was opened.'
            : errorSummary(error) ?? 'The request failed.'}
        </Text>
      </Alert>
    )
  }

  if (isLoading) {
    return (
      <Group gap="xs">
        <Loader size="xs" />
        <Text size="sm" c="dimmed">
          Opening job {jobId}…
        </Text>
      </Group>
    )
  }

  return null
}

function JobTable({
  jobs,
  onRun,
  showLevel,
  showCategory,
  canRun,
  refusalFor,
}: {
  jobs: EtlJob[]
  onRun: (job: EtlJob) => void
  showLevel?: boolean
  /** For the catch-all group, whose heading no longer says what the jobs are. */
  showCategory?: boolean
  /** From the catalogue's server-evaluated permission, never from a token claim. */
  canRun: boolean
  /** Why the server would refuse this job right now (permission or halt), or null. */
  refusalFor: (job: EtlJob) => string | null
}) {
  return (
    <Table striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Job</Table.Th>
          {showCategory && <Table.Th w={130}>Category</Table.Th>}
          {showLevel && <Table.Th w={90}>Level</Table.Th>}
          <Table.Th>Schedule</Table.Th>
          <Table.Th w={120} />
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {jobs.map((job) => {
          const refusal = refusalFor(job)
          return (
            <Table.Tr key={job.jobId}>
              <Table.Td>
                <Text fw={500}>{job.displayName}</Text>
                {job.description && (
                  <Text size="xs" c="dimmed">
                    {job.description}
                  </Text>
                )}
              </Table.Td>
              {showCategory && (
                <Table.Td>
                  <Badge size="sm" variant="default">
                    {job.category}
                  </Badge>
                </Table.Td>
              )}
              {showLevel && (
                <Table.Td>
                  <Badge size="sm" variant="default">
                    {job.levelNumber}
                  </Badge>
                </Table.Td>
              )}
              <Table.Td>
                {job.isManualOnly ? (
                  <Badge size="sm" variant="light" color="gray">
                    Manual only
                  </Badge>
                ) : (
                  <Text size="xs" ff="monospace">
                    {job.cronExpression}
                  </Text>
                )}
              </Table.Td>
              <Table.Td>
                {/* Disabled rather than hidden, with the server's reason on hover: the row still
                    reads as "a job you could run", which is the truth, and the tooltip says what is
                    missing — the role, or the halt that refuses this category. A vanished control
                    teaches nothing. */}
                <Tooltip label={refusal} disabled={!refusal} multiline w={280} withArrow>
                  <Button
                    size="xs"
                    variant="light"
                    leftSection={
                      !canRun ? (
                        <IconLock size={14} />
                      ) : refusal ? (
                        <IconHandStop size={14} />
                      ) : (
                        <IconPlayerPlay size={14} />
                      )
                    }
                    onClick={() => onRun(job)}
                    disabled={!!refusal}
                    data-disabled={!!refusal || undefined}
                  >
                    Run
                  </Button>
                </Tooltip>
              </Table.Td>
            </Table.Tr>
          )
        })}
      </Table.Tbody>
    </Table>
  )
}

/** The request history: loading, failed and empty are three different things, and look it. */
function RequestHistory({
  requests,
  isLoading,
  error,
  jobNames,
}: {
  requests: EtlJobTriggerRequest[] | undefined
  isLoading: boolean
  error: unknown
  jobNames: Map<string, string>
}) {
  const view = requestHistoryView(requests, isLoading, error != null)

  if (view === 'failed') {
    return errorKind(error) === 'forbidden' ? (
      <RestrictedState surface="Run requests" />
    ) : (
      <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Could not load run requests — this is not the same as none">
        <Text size="sm">{errorSummary(error) ?? 'The request failed.'}</Text>
      </Alert>
    )
  }

  if (view === 'loading' || !requests) {
    return (
      <Group gap="xs">
        <Loader size="xs" />
        <Text size="sm" c="dimmed">
          Loading run requests…
        </Text>
      </Group>
    )
  }

  const refreshFailed = error != null && (
    <Alert color="yellow" variant="light" title="Could not refresh run requests">
      <Text size="sm">
        {errorSummary(error) ?? 'The request failed.'} This is the list as last loaded.
      </Text>
    </Alert>
  )

  if (view === 'empty') {
    return (
      <Stack gap="xs">
        {refreshFailed}
        <Card withBorder padding="sm">
          <Text size="sm">No run requests recorded.</Text>
          <Text size="xs" c="dimmed">
            Requests are kept for 90 days by default.
          </Text>
        </Card>
      </Stack>
    )
  }

  return (
    <Stack gap="xs">
      {refreshFailed}
      <Card withBorder padding="sm">
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Job</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Asked for</Table.Th>
              <Table.Th>Requested</Table.Th>
              <Table.Th>By</Table.Th>
              <Table.Th>Run</Table.Th>
              <Table.Th>Hangfire</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {requests.map((r) => (
              <RequestRow key={r.requestId} request={r} displayName={jobNames.get(r.jobId) ?? null} />
            ))}
          </Table.Tbody>
        </Table>
      </Card>
    </Stack>
  )
}

function RequestRow({ request, displayName }: { request: EtlJobTriggerRequest; displayName: string | null }) {
  const navigate = useNavigate()
  const color =
    request.status === 'Enqueued'
      ? 'teal'
      : request.status === 'Requested'
        ? 'blue'
        : 'red'

  const runLink = requestRunLink(request)
  // The shared outcome map, drawn by the badge Execution History draws it with: the run link opens that page.
  const outcome = requestRunOutcome(request)
  const jobUrl = request.hangfireJobId ? hangfireJobUrl(request.hangfireJobId) : null
  // A Failed request's detail is served only to an administrator (D8): withheld, it reads as such, never as blank.
  const statusDetail = requestStatusDetailText(request)

  return (
    <Table.Tr>
      <Table.Td>
        {displayName && <Text size="sm">{displayName}</Text>}
        <Text size="xs" ff="monospace" c={displayName ? 'dimmed' : undefined}>
          {request.jobId}
        </Text>
      </Table.Td>
      <Table.Td>
        <Badge size="sm" color={color} variant="light">
          {request.status}
        </Badge>
        {statusDetail && (
          <Text size="xs" c="dimmed" data-state={request.statusDetailWithheld === true ? 'detail-withheld' : undefined}>
            {statusDetail}
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        <Text size="xs" ff="monospace">
          {formatRequestParameters(request.parameters)}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="xs">{whenLabel(request.requestedAt)}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="xs">{request.requestedBy ?? NOT_MEASURED}</Text>
      </Table.Td>
      <Table.Td>
        {runLink ? (
          <Stack gap={2} align="flex-start">
            <Tooltip label={`Open in ${runLink.destination}`} withArrow>
              <Anchor component="button" type="button" size="xs" ff="monospace" onClick={() => navigate(runLink.path)}>
                {runLink.label}
              </Anchor>
            </Tooltip>
            {outcome && <OutcomeBadge presentation={outcome} />}
          </Stack>
        ) : (
          <Tooltip label={runDashCaption(request.status)} multiline w={340} withArrow>
            <Text size="xs" c="dimmed" tabIndex={0} style={{ cursor: 'help' }}>
              {NOT_MEASURED}
            </Text>
          </Tooltip>
        )}
      </Table.Td>
      <Table.Td>
        {request.hangfireJobId ? (
          jobUrl ? (
            <Anchor href={jobUrl} target="_blank" rel="noopener noreferrer" size="xs">
              {request.hangfireJobId} <IconExternalLink size={11} />
            </Anchor>
          ) : (
            <Text size="xs" ff="monospace">
              {request.hangfireJobId}
            </Text>
          )
        ) : (
          <Text size="xs" c="dimmed">
            {NOT_MEASURED}
          </Text>
        )}
      </Table.Td>
    </Table.Tr>
  )
}

export default JobsPage
